import { z } from 'zod'

const phoneRegex = /^[\d\s()+-]{10,}$/

/* ---------------- Registration ---------------- */

export const playerInfoSchema = z.object({
  firstName: z.string().min(1, 'Required'),
  middleName: z.string().optional(),
  lastName: z.string().min(1, 'Required'),
  age: z.coerce.number().int().min(3).max(19),
  birthdate: z.string().min(1, 'Required'),
  gender: z.enum(['Boy', 'Girl'], { required_error: 'Please select' }),
  newPlayer: z.enum(['Yes', 'No'], { required_error: 'Please select' }),
})

export const parentInfoSchema = z.object({
  parentName: z.string().min(1, 'Required'),
  phone: z.string().regex(phoneRegex, 'Enter a valid phone'),
  email: z.string().email('Enter a valid email'),
  address: z.string().min(1, 'Required'),
  city: z.string().min(1, 'Required'),
  state: z.string().min(1, 'Required'),
  zip: z.string().min(3, 'Required'),
  country: z.string().min(1, 'Required'),
})

export const emergencySchema = z.object({
  emFirst: z.string().min(1, 'Required'),
  emLast: z.string().min(1, 'Required'),
  emRelation: z.string().min(1, 'Required'),
  emPhone: z.string().regex(phoneRegex, 'Enter a valid phone'),
  medical: z.string().optional(),
})

export const leagueSchema = z.object({
  league: z.enum(['Cal South', 'SoCal Spring', 'US Club Soccer'], { required_error: 'Please select a league' }),
  program: z.string().min(1, 'Please select a program'),
  waiver: z.boolean().refine((v) => v === true, 'Please accept the waiver'),
  photo: z.boolean().optional(),
})

export const registrationSchema = playerInfoSchema
  .merge(parentInfoSchema)
  .merge(emergencySchema)
  .merge(leagueSchema)
export type RegistrationSchema = z.infer<typeof registrationSchema>

/* ---------------- Contact ---------------- */

export const contactSchema = z.object({
  name: z.string().min(1, 'Required'),
  email: z.string().email('Enter a valid email'),
  subject: z.string().min(1, 'Required'),
  message: z.string().optional(),
})
export type ContactSchema = z.infer<typeof contactSchema>

/* ---------------- Scholarship ---------------- */

export const scholarshipSchema = z.object({
  firstName: z.string().min(1, 'Required'),
  lastName: z.string().min(1, 'Required'),
  dob: z.string().min(1, 'Required'),
  email: z.string().email('Enter a valid email'),
  gender: z.enum(['Boy', 'Girl', 'Other']),
  phone: z.string().regex(phoneRegex, 'Enter a valid phone'),
  address: z.string().min(1, 'Required'),
  country: z.string().min(1, 'Required'),
  school: z.string().min(1, 'Required'),
  areaOfStudy: z.string().min(1, 'Required'),
  intendedCollege: z.string().min(1, 'Required'),
  gradYear: z.coerce.number().int().min(2024).max(2035),
  gpa: z.coerce.number().min(0).max(5),
  major: z.string().min(1, 'Required'),
  position1: z.string().min(1, 'Required'),
  position2: z.string().optional(),
  duration: z.string().min(1, 'Required'),
  hearAbout: z.string().min(1, 'Required'),
  whyApply: z.string().min(20, 'At least 20 characters'),
  income1: z.coerce.number().min(0),
  income2: z.coerce.number().min(0).optional(),
  otherAid: z.string().optional(),
  rec1Name: z.string().min(1, 'Required'),
  rec1Email: z.string().email('Enter a valid email'),
  rec1Phone: z.string().regex(phoneRegex, 'Enter a valid phone'),
  rec2Name: z.string().min(1, 'Required'),
  rec2Email: z.string().email('Enter a valid email'),
  rec2Phone: z.string().regex(phoneRegex, 'Enter a valid phone'),
  agree1: z.boolean().refine((v) => v, 'Required'),
  agree2: z.boolean().refine((v) => v, 'Required'),
  agree3: z.boolean().refine((v) => v, 'Required'),
})
export type ScholarshipSchema = z.infer<typeof scholarshipSchema>

/* ---------------- Donate ---------------- */

export const donateSchema = z.object({
  amount: z.coerce.number().min(1, 'Minimum $1'),
  frequency: z.enum(['one-time', 'monthly']),
  name: z.string().optional(),
  email: z.string().email('Enter a valid email').or(z.literal('')).optional(),
  anonymous: z.boolean().default(false),
})
export type DonateSchema = z.infer<typeof donateSchema>
